import { stripePlans } from "@gosaas/types/stripe";
import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { PlanCheckoutConfigurator } from "@/components/billing/plan-checkout-configurator";
import { BillingFaqs } from "@/components/faqs/billing";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { buttonVariants } from "@/components/ui/button";
import { PricingCard, PricingTable } from "@/components/ui/pricing-table";
import { isBillingApiPricingEnabled, isModuleEnabled } from "@/lib/modules";
import { cn } from "@/lib/utils";
import {
	getStaggeredAnimationDelayStyle,
	MARKETING_SECTION_ANIM_BASE,
} from "@/utils/animation";
import { MARKETING_QUERY_ADDON_TIERS } from "@/utils/api-pricing";
import { StripePlansToProducts } from "@/utils/billing";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_marketing/pricing")({
	beforeLoad: () => {
		if (!isModuleEnabled("billing")) {
			throw redirect({ to: "/" });
		}
	},
	component: App,
	head: () =>
		seo({
			title: "Pricing",
			description:
				"Flexible monthly and annual plans for email validation API usage. Compare quotas, team seats, and features—toggle billing cycles to see what fits your volume.",
			keywords:
				"email validation pricing, email API plans, validation API subscription, monthly pricing, annual pricing",
		}),
});

const PricingContent = ({
	isAnnual,
	setIsAnnual,
}: {
	isAnnual: boolean;
	setIsAnnual: (isAnnual: boolean) => void;
}) => {
	const products = StripePlansToProducts;
	const amplifyPlan = stripePlans.find((p) => p.name === "Team");
	const [selectedProductId, setSelectedProductId] = useState<string | null>(
		amplifyPlan?.productId ?? stripePlans[0]?.productId ?? null,
	);
	const [addonIndex, setAddonIndex] = useState(0);
	const selectedPlan = stripePlans.find(
		(p) => p.productId === selectedProductId,
	);
	const showConfigurator =
		isBillingApiPricingEnabled() && selectedPlan !== undefined;

	return (
		<div className={cn(MARKETING_SECTION_ANIM_BASE, "delay-200")}>
			<PricingTable
				products={products}
				showFeatures={true}
				isAnnual={isAnnual}
				setIsAnnual={setIsAnnual}
			>
				{products.map((product, index) => {
					const plan = stripePlans.find((p) => p.productId === product.id);
					if (!plan) return null;
					const isSelected = selectedProductId === product.id;
					return (
						<div
							key={product.id}
							className={MARKETING_SECTION_ANIM_BASE}
							style={getStaggeredAnimationDelayStyle(index, 0.4)}
						>
							<PricingCard
								productId={product.id}
								isSelected={isSelected}
								onSelect={() => {
									setSelectedProductId(product.id);
									setAddonIndex(0);
								}}
								showBuyButton={false}
							/>
						</div>
					);
				})}
			</PricingTable>

			{showConfigurator && selectedPlan && (
				<PlanCheckoutConfigurator
					plan={selectedPlan}
					isAnnual={isAnnual}
					addonTiers={MARKETING_QUERY_ADDON_TIERS}
					addonIndex={addonIndex}
					onAddonIndexChange={setAddonIndex}
					showCheckoutButton={false}
					footer={
						<div className="mt-6 space-y-3">
							<Link
								to="/auth/register"
								className={cn(buttonVariants(), "w-full py-6 text-base")}
							>
								Start free trial
							</Link>
							<p className="text-center text-muted-foreground text-xs">
								Configure add-ons again after signup on your team billing page.
							</p>
						</div>
					}
				/>
			)}
		</div>
	);
};

export function App() {
	const [isAnnual, setIsAnnual] = useState(false);

	return (
		<MarketingShell>
			<section className="pt-20 pb-16 sm:pt-28 sm:pb-24">
				<MarketingContainer>
					<MarketingHeader
						title="Pricing"
						heading="Pay for validation volume, not seat math"
						description="Pick a plan, dial in API volume, and see your total before you sign up. Every tier includes the full detection stack."
					/>
					<PricingContent isAnnual={isAnnual} setIsAnnual={setIsAnnual} />

					<div
						className={cn(
							"mt-16 sm:mt-20",
							MARKETING_SECTION_ANIM_BASE,
							"delay-500",
						)}
					>
						<BillingFaqs />
					</div>
				</MarketingContainer>
			</section>
		</MarketingShell>
	);
}
